import React, { useState } from "react";

import { ButtonStandard, InputWrapper, ModalBase, SingleSelectInput, Text } from "@performyard/py-components";
import type { VideoConferencingProviderEnum } from "@performyard/service-contracts";

import { VIDEO_CONFERENCING_PROVIDER_CONFIG } from "../../../../../components/VideoConferencing";

import { useEditWorkspaceMeeting } from "../../../../../api/hooks";

type AddVideoConferencingModalProps = {
	isOpen: boolean;
	onClose: () => void;
	meetingId: string;
	connectedProviders: { provider: VideoConferencingProviderEnum }[];
};

/**
 * Provider picker for adding video conferencing to an existing meeting when the user has more than
 * one provider connected (PY-12435). With a single connected provider the options menus attach it
 * directly and this modal is skipped. Failures surface as a toast (same as the single-provider
 * path — the retry/continue error modal is scoped to meeting creation); only success closes the
 * modal, so Add doubles as retry.
 */
export const AddVideoConferencingModal = ({
	isOpen,
	onClose,
	meetingId,
	connectedProviders,
}: AddVideoConferencingModalProps): React.ReactElement => {
	const [selectedProvider, setSelectedProvider] = useState<VideoConferencingProviderEnum | null>(null);

	const handleClose = (): void => {
		setSelectedProvider(null);
		onClose();
	};

	const { mutate: editMeeting, isPending } = useEditWorkspaceMeeting(handleClose);

	const handleAdd = (): void => {
		if (!selectedProvider) return;
		editMeeting({ meetingId, data: { videoConferencingProvider: selectedProvider } });
	};

	return (
		<ModalBase isOpen={isOpen} onClose={isPending ? () => {} : handleClose} dangerouslySetClassName="w-[432px]">
			<div className="px-6 pb-6 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Add Video Conferencing
				</Text>
			</div>
			<div className="px-6 pb-6">
				<InputWrapper id="add-video-conferencing-select" label="Video Conferencing">
					<SingleSelectInput<VideoConferencingProviderEnum>
						options={connectedProviders.map(({ provider }) => ({
							value: provider,
							label: VIDEO_CONFERENCING_PROVIDER_CONFIG[provider].shortName,
						}))}
						value={selectedProvider ?? undefined}
						onChange={(value) => setSelectedProvider(value)}
						placeholder="Make a selection"
						disabled={isPending}
						data-testid="add-video-conferencing-select"
					/>
				</InputWrapper>
			</div>
			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={handleClose}
						disabled={isPending}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						size="large"
						color="primary"
						onClick={handleAdd}
						disabled={isPending || !selectedProvider}
						data-testid="add-video-conferencing-submit-button"
					>
						Add
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};
